import { Box } from "@mui/material";
import { OrderPriceSummary } from "components/global/components/OrderPriceSummary";
import { BlurredBackground } from "components/primitives/BlurredBackground";
import { DrawerToggleIndicator } from "components/primitives/DrawerToggleIndicator";
import { SwipeableArea } from "components/primitives/SwipeableArea";
import { useMixpanelTrackers } from "hooks/useMixpanelTrackers";
import { useAppSelector } from "hooks/utilsHooks/storeHooks";
import { useEffect, useRef } from "react";
import {
	selectActiveLocalTransaction,
	selectIsEmptyOrder,
	selectOrderPaymentMethod,
	selectOrderSummary,
} from "slices/transactionSlice";
import { TransactionType } from "types";
import { ORDER_STEP_5_SMALL_ORDER_LIMIT } from "utils/designTokens";
import { PaymentMethodType } from "../../gql/graphql";
import { OrderDishesList } from "./components/OrderDishesList";
import { Order5DrawerHeader } from "./components/OrderStep5Header";
import { DrawerWrapper, OrderDishesWrapper, OrderItemBlockWrapper } from "./components/orderStepDrawerStyles";
import { RatingBox } from "./components/RatingBox";
import { EmptyOrderState } from "./EmptyOrderState";
import type { BaseOrderStepProps } from "./OrderStep2";

type OrderStep5Props = {
	resetOrder: () => void;
	doorHasBeenOpened: boolean;
};

export const OrderStep5 = ({
	open,
	resetOrder,
	toggleDrawer,
	doorHasBeenOpened,
}: BaseOrderStepProps & OrderStep5Props) => {
	const orderSummary = useAppSelector(selectOrderSummary);
	const transaction = useAppSelector(selectActiveLocalTransaction);
	const isEmptyOrder = useAppSelector(selectIsEmptyOrder);
	const paymentMethodType = useAppSelector(selectOrderPaymentMethod);
	const {
		trackStockCheckEnded,
		trackFinishedOrderScreen,
		trackFinishedOrder,
		trackFinishedEmptyOrder,
		trackDoorNotOpened,
	} = useMixpanelTrackers();
	const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	const clearTimeoutHandler = () => {
		clearTimeout(timerRef.current);
		return () => clearTimeout(timerRef.current);
	};

	useEffect(() => {
		timerRef.current = setTimeout(() => {
			resetOrder();
		}, 10_000);
		return () => {
			clearTimeout(timerRef.current);
		};
	}, [resetOrder]);

	// UseEffect hook to track the finished order screen and the finished order event
	const hasTrackedOrderFinishedRef = useRef(false);
	useEffect(() => {
		if (hasTrackedOrderFinishedRef.current) {
			return;
		}

		// Only track if we have a transaction - otherwise wait for it
		if (!transaction) {
			return;
		}

		// Refills emit their own analytics from DeviceContext (trackRefillFinished); skip the
		// order-specific events here so a refill reaching step 5 doesn't double-track.
		if (transaction.type !== TransactionType.Order) {
			hasTrackedOrderFinishedRef.current = true;
			return;
		}

		hasTrackedOrderFinishedRef.current = true;

		trackStockCheckEnded();
		trackFinishedOrderScreen(isEmptyOrder);
		if (isEmptyOrder) {
			if (!doorHasBeenOpened) {
				trackDoorNotOpened();
			}
			trackFinishedEmptyOrder(transaction.id);
		} else {
			trackFinishedOrder(transaction.id, transaction, orderSummary);
		}

		return () => {
			hasTrackedOrderFinishedRef.current = false;
		};
	}, [
		trackStockCheckEnded,
		trackFinishedOrderScreen,
		trackFinishedOrder,
		trackFinishedEmptyOrder,
		trackDoorNotOpened,
		isEmptyOrder,
		transaction,
		orderSummary,
		doorHasBeenOpened,
	]);

	const isTopUpPayment =
		paymentMethodType === PaymentMethodType.NativePayTopUp || paymentMethodType === PaymentMethodType.PaymentCardTopUp;

	const { orderDishTypes } = orderSummary || {};

	return (
		<>
			<SwipeableArea orderstep={5} open={open}>
				{!open && <BlurredBackground orderStep={5} open={open} />}
				<DrawerToggleIndicator open={open} toggleDrawer={toggleDrawer} />
				<Order5DrawerHeader isEmptyOrder={isEmptyOrder} paymentMethod={paymentMethodType} />
			</SwipeableArea>
			<DrawerWrapper
				sx={{
					...(open && {
						marginTop: isTopUpPayment ? "240px" : "220px",
					}),
				}}
			>
				{isEmptyOrder ? (
					<EmptyOrderState transactionId={transaction?.id || null} />
				) : (
					<OrderItemBlockWrapper>
						{orderSummary && orderDishTypes && (
							<>
								<OrderDishesWrapper
									maxHeight={orderDishTypes.length > ORDER_STEP_5_SMALL_ORDER_LIMIT ? "600px" : "320px"}
								>
									<OrderDishesList smallOrderLimit={ORDER_STEP_5_SMALL_ORDER_LIMIT} />
								</OrderDishesWrapper>

								<Box>
									<OrderPriceSummary orderPrice={orderSummary.orderPrice?.total} />
								</Box>
							</>
						)}
						{transaction && !isEmptyOrder && (
							<RatingBox
								resetOrder={resetOrder}
								transactionId={transaction.id}
								onNegativeFeedbackClick={() => {
									clearTimeoutHandler();
								}}
							/>
						)}
					</OrderItemBlockWrapper>
				)}
			</DrawerWrapper>
		</>
	);
};
